import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, Pressable, SafeAreaView, StatusBar, Modal, ScrollView, Platform } from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter, useIsFocused } from 'expo-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import { checkInByQR, getEventInstance, checkInPerson } from '@/data/events';
import { DateTime } from 'luxon';
import { Query } from '@/utils/react-query/Queries';
import RegistrantDetailModal from '@/components/Events/RegistrantDetailModal';

// Errors can carry longer server messages (e.g. pending-registration notice) — give users time to read
const RESULT_TIMEOUT_ERROR = 5000;

function getAgeFromDateOfBirth(dateOfBirth) {
    if (!dateOfBirth) return null;
    const birth = DateTime.fromISO(dateOfBirth);
    if (!birth.isValid) return null;
    const now = DateTime.now();
    const age = Math.floor(now.diff(birth, 'years').years);
    return age >= 0 ? age : null;
}

function formatTargetDate(startTime) {
    if (!startTime) return '';
    const dt = DateTime.fromISO(startTime);
    if (!dt.isValid) return '';
    return dt.toFormat('ccc, MMM d · h:mm a');
}

export default function ScanRegistrationQRScreen() {
    const router = useRouter();
    const { eventInstanceID, defaultLocationID } = useLocalSearchParams();

    // QR scanning is not supported on web
    if (Platform.OS === 'web') {
        return (
            <SafeAreaView style={styles.permissionContainer}>
                <StatusBar barStyle="dark-content" />
                <Unicon name="camera" size={64} colorVariant="textSecondary" />
                <Text style={styles.permissionTitle}>QR Scanning Not Available</Text>
                <Text style={styles.permissionText}>
                    QR code scanning is only available on mobile devices. Please use the mobile app to scan registration QR codes.
                </Text>
                <SolidButton text="Go Back" onPress={() => router.back()} type="primary" size="md" />
            </SafeAreaView>
        );
    }

    const [permission, requestPermission] = useCameraPermissions();
    const isFocused = useIsFocused();
    const [scanned, setScanned] = useState(false);
    const [lastScanResult, setLastScanResult] = useState(null);
    const [selectedLocationID, setSelectedLocationID] = useState(defaultLocationID || null);
    const [groupCheckInData, setGroupCheckInData] = useState(null);
    const [selectedRegistrantIDs, setSelectedRegistrantIDs] = useState(new Set());
    const [checkedInRegistrantIDs, setCheckedInRegistrantIDs] = useState(new Set());
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const [detailRegistrant, setDetailRegistrant] = useState(null);
    const [targetEventInstanceID, setTargetEventInstanceID] = useState(null);
    const [targetEventName, setTargetEventName] = useState(null);
    const [targetStartTime, setTargetStartTime] = useState(null);
    const dismissTimer = useRef(null);

    const { data: eventInstance } = useQuery({
        queryKey: ['eventInstance', eventInstanceID],
        queryFn: () => getEventInstance(eventInstanceID),
        enabled: !!eventInstanceID,
        staleTime: 5 * 60 * 1000,
    });

    const { data: targetEventInstance } = useQuery({
        queryKey: ['eventInstance', targetEventInstanceID],
        queryFn: () => getEventInstance(targetEventInstanceID),
        enabled: !!targetEventInstanceID && targetEventInstanceID !== eventInstanceID,
        staleTime: 5 * 60 * 1000,
    });

    const locations = eventInstance?.Locations ?? [];

    const checkInLocations = targetEventInstanceID && targetEventInstanceID !== eventInstanceID
        ? (targetEventInstance?.Locations ?? [])
        : locations;

    // Effective location: 0 → null, 1 → auto, 2+ → picker (selectedLocationID)
    const effectiveLocationID = locations.length === 0
        ? null
        : locations.length === 1
            ? locations[0].id
            : selectedLocationID;

    // Fetch linked registrant paths for the detail modal
    const { data: registrantPaths = [] } = Query.eventInstances.registrantPaths({
        eventInstanceID,
        options: { enabled: !!eventInstanceID },
    });

    // Sync when defaultLocationID changes (e.g. on first load)
    useEffect(() => {
        if (defaultLocationID && !selectedLocationID) {
            setSelectedLocationID(defaultLocationID);
        }
    }, [defaultLocationID]);

    const resetScanner = useCallback(() => {
        setScanned(false);
        setLastScanResult(null);
        setTargetEventInstanceID(null);
        setTargetEventName(null);
        setTargetStartTime(null);
        if (dismissTimer.current) clearTimeout(dismissTimer.current);
    }, []);

    const toggleRegistrantSelection = useCallback((personID) => {
        // Don't allow selecting already checked-in people
        if (checkedInRegistrantIDs.has(personID)) return;

        setSelectedRegistrantIDs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(personID)) {
                newSet.delete(personID);
            } else {
                newSet.add(personID);
            }
            return newSet;
        });
    }, [checkedInRegistrantIDs]);

    const resetGroupCheckIn = useCallback(() => {
        setGroupCheckInData(null);
        setSelectedRegistrantIDs(new Set());
        setCheckedInRegistrantIDs(new Set());
        resetScanner();
    }, [resetScanner]);

    const scheduleReset = useCallback((timeout) => {
        if (dismissTimer.current) clearTimeout(dismissTimer.current);
        dismissTimer.current = setTimeout(() => {
            resetScanner();
        }, timeout);
    }, [resetScanner]);

    const checkInByQRMutation = useMutation({
        mutationFn: checkInByQR,
        onSuccess: (result) => {
            setTargetEventInstanceID(result.targetEventInstanceID || null);
            setTargetEventName(result.targetEventName || null);
            setTargetStartTime(result.targetStartTime || null);
            if (result.group) {
                // Group QR code - show modal with all registrants
                setGroupCheckInData(result);
                // Select ALL registrants by default — staff deselect as needed.
                const defaultIDs = new Set(
                    result.group.registrants.map(r => r.personID)
                );
                setSelectedRegistrantIDs(defaultIDs);
            } else if (result.person) {
                // Badge code — single person. Open the registrant detail modal directly.
                // Do NOT set groupCheckInData. No auto-select / auto-check-in.
                setDetailRegistrant(result.person);
            }
        },
        onError: (error) => {
            console.error('checkInByQRMutation error:', error);
            setLastScanResult({
                success: false,
                message: error?.message || 'Failed to verify QR code',
                personName: null,
                alreadyCheckedIn: false,
            });
            scheduleReset(RESULT_TIMEOUT_ERROR);
        },
    });

    const handleBarCodeScanned = useCallback(({ data }) => {
        if (scanned) return;
        setScanned(true);

        try {
            // Group QR codes are prefixed; badge codes are arbitrary payloads.
            if (data.startsWith('REACH:CHECKIN:')) {
                const payload = data.slice('REACH:CHECKIN:'.length);
                const decoded = atob(payload);
                const parts = decoded.split(':');

                if (parts.length !== 3) {
                    setLastScanResult({ success: false, message: 'Invalid QR code format' });
                    setScanned(false);
                    scheduleReset(RESULT_TIMEOUT_ERROR);
                    return;
                }

                const [groupID, scannedEventInstanceID, token] = parts;

                if (!groupID || !scannedEventInstanceID || !token) {
                    setLastScanResult({ success: false, message: 'Invalid QR code format' });
                    setScanned(false);
                    scheduleReset(RESULT_TIMEOUT_ERROR);
                    return;
                }

                checkInByQRMutation.mutate({ qrPayload: payload, scannerEventInstanceID: eventInstanceID });
            } else {
                // Badge code — send the raw payload verbatim; backend resolves to a single person.
                checkInByQRMutation.mutate({ qrPayload: data, scannerEventInstanceID: eventInstanceID });
            }
        } catch (error) {
            console.error('QR scan error:', error);
            setLastScanResult({ success: false, message: 'Failed to read QR code' });
            setScanned(false);
            scheduleReset(RESULT_TIMEOUT_ERROR);
        }
    }, [scanned, eventInstanceID, checkInByQRMutation, scheduleReset]);

    // Permission handling
    if (!permission?.granted) {
        return (
            <SafeAreaView style={styles.permissionContainer}>
                <StatusBar barStyle="dark-content" />
                <Unicon name="camera" size={64} colorVariant="textSecondary" />
                <Text style={styles.permissionTitle}>Camera Access Required</Text>
                <Text style={styles.permissionText}>
                    Please allow camera access to scan registration QR codes
                </Text>
                <SolidButton text="Grant Permission" onPress={requestPermission} type="primary" size="md" />
                <Pressable onPress={() => router.back()} style={styles.backButtonAlt}>
                    <Text style={styles.backButtonText}>Go Back</Text>
                </Pressable>
            </SafeAreaView>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor="black" />

            {/* Full screen camera — never unmounts/remounts */}
            <CameraView
                style={styles.camera}
                facing="back"
                barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
                onBarcodeScanned={scanned ? undefined : handleBarCodeScanned}
            />

            {/* Dark overlay with center cutout */}
            <View style={styles.overlay} pointerEvents="none">
                <View style={styles.darkArea} />
                <View style={styles.middleRow}>
                    <View style={styles.darkArea} />
                    <View style={styles.scanFrame}>
                        <View style={[styles.corner, styles.cornerTopLeft]} />
                        <View style={[styles.corner, styles.cornerTopRight]} />
                        <View style={[styles.corner, styles.cornerBottomLeft]} />
                        <View style={[styles.corner, styles.cornerBottomRight]} />
                    </View>
                    <View style={styles.darkArea} />
                </View>
                <View style={styles.darkArea} />
            </View>

            {/* Back button */}
            <Pressable onPress={() => router.back()} style={styles.backButton}>
                <Unicon name="chevron-back" size={24} style={{ color: 'white' }} />
            </Pressable>

            {/* Instruction text */}
            <Text style={styles.instructionText} pointerEvents="none">
                Position QR code within the frame
            </Text>

            {/* Processing indicator — small, above scan frame */}
            {scanned && (!lastScanResult || lastScanResult.loading) && !groupCheckInData && !detailRegistrant && (
                <View style={styles.toastContainer} pointerEvents="none">
                    <View style={styles.toastCard}>
                        <Text style={styles.toastText}>Checking in...</Text>
                    </View>
                </View>
            )}

            {/* Result toast — above scan frame */}
            {lastScanResult && !lastScanResult.loading && (
                <View style={styles.toastContainer} pointerEvents="none">
                    <View style={styles.toastCard}>
                        {lastScanResult.success ? (
                            <>
                                <Unicon name="checkmark-circle" size={24} colorVariant="success" />
                                <Text style={styles.toastText}>
                                    {lastScanResult.alreadyCheckedIn ? 'Already checked in' : 'Checked in'}
                                    {lastScanResult.personName ? `: ${lastScanResult.personName}` : ''}
                                </Text>
                            </>
                        ) : (
                            <>
                                <Unicon name="alert-circle" size={24} colorVariant="error" />
                                <Text style={styles.toastText}>
                                    {lastScanResult.message || 'Check-in failed'}
                                </Text>
                            </>
                        )}
                    </View>
                </View>
            )}

            {/* Group check-in modal — hidden while a pushed screen (add-registration-code)
                is on top so the RN Modal can't cover it (Android), restored on return */}
            <Modal
                visible={!!groupCheckInData && isFocused}
                transparent
                animationType="slide"
                onRequestClose={resetGroupCheckIn}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Group Check-In</Text>
                            <Pressable
                                onPress={resetGroupCheckIn}
                                style={styles.modalCloseButton}
                            >
                                <Unicon name="close" size={24} style={styles.modalCloseIcon} />
                            </Pressable>
                        </View>

                        <ScrollView style={styles.modalScroll}>
                            <Text style={styles.modalSubtitle}>
                                {groupCheckInData?.group?.registrants?.length || 0} person{groupCheckInData?.group?.registrants?.length > 1 ? 's' : ''} registered
                            </Text>

                            {targetEventInstanceID && targetEventInstanceID !== eventInstanceID && (
                                <View style={styles.targetBanner}>
                                    <Unicon name="calendar-outline" size={14} colorVariant="primary" />
                                    <Text style={styles.targetBannerText}>
                                        Checking in to {targetEventName || 'extended event'}{targetStartTime ? ` · ${formatTargetDate(targetStartTime)}` : ''}
                                    </Text>
                                </View>
                            )}

                            {groupCheckInData?.group?.registrants?.map((registrant, index) => {
                                const isSelected = selectedRegistrantIDs.has(registrant.personID);
                                const isCheckedIn = checkedInRegistrantIDs.has(registrant.personID);
                                const age = getAgeFromDateOfBirth(registrant.dateOfBirth);
                                return (
                                    <View
                                        key={registrant.personID || index}
                                        style={[
                                            styles.registrantCard,
                                            isSelected && styles.registrantCardSelected,
                                            isCheckedIn && styles.registrantCardCheckedIn
                                        ]}
                                    >
                                        <Pressable
                                            style={styles.registrantMain}
                                            onPress={() => toggleRegistrantSelection(registrant.personID)}
                                            disabled={isCheckedIn}
                                        >
                                            <View style={styles.registrantCheckbox}>
                                                {isCheckedIn ? (
                                                    <Unicon name="checkmark-circle" size={22} colorVariant="success" />
                                                ) : (
                                                    <Unicon name={isSelected ? "checkmark-circle" : "ellipse-outline"} size={22} colorVariant="success" />
                                                )}
                                            </View>
                                            <View style={styles.registrantInfo}>
                                                <Text style={[styles.registrantName, isCheckedIn && styles.registrantNameCheckedIn]}>
                                                    {registrant.firstName} {registrant.lastName}
                                                    {age !== null && <Text style={styles.registrantAge}> ({age})</Text>}
                                                </Text>
                                                {isCheckedIn && (
                                                    <Text style={styles.checkedInLabel}>Checked In</Text>
                                                )}
                                            </View>
                                        </Pressable>
                                        <Pressable
                                            style={styles.registrantInfoButton}
                                            onPress={() => setDetailRegistrant(registrant)}
                                            testID={`registrant-info-${registrant.personID}`}
                                        >
                                            <Unicon name="information-circle-outline" size={22} colorVariant="primary" />
                                        </Pressable>
                                    </View>
                                );
                            })}

                            {locations.length >= 2 && (
                                <View style={styles.modalLocationSection}>
                                    <Text style={[styles.modalLocationLabel, styles.modalLocationLabelCentered]}>Check-in location:</Text>
                                    <View style={styles.modalLocationPills}>
                                        {locations.map(loc => (
                                            <Pressable
                                                key={loc.id}
                                                style={[styles.modalLocationPill, selectedLocationID === loc.id && styles.modalLocationPillSelected]}
                                                onPress={() => setSelectedLocationID(loc.id)}
                                            >
                                                <Text style={[styles.modalLocationPillText, selectedLocationID === loc.id && styles.modalLocationPillTextSelected]}>
                                                    {loc.name}
                                                </Text>
                                            </Pressable>
                                        ))}
                                    </View>
                                </View>
                            )}
                        </ScrollView>

                        <View style={styles.modalButtonsColumn}>
                            <SolidButton
                                text="Check In Selected"
                                type="secondary"
                                size="lg"
                                disabled={selectedRegistrantIDs.size === 0 || isCheckingIn}
                                processing={isCheckingIn}
                                onPress={async () => {
                                    setIsCheckingIn(true);
                                    try {
                                        // Check in selected registrants
                                        const selectedRegistrants = groupCheckInData.group.registrants.filter(r =>
                                            selectedRegistrantIDs.has(r.personID)
                                        );
                                        await Promise.all(selectedRegistrants.map(registrant =>
                                            checkInPerson({
                                                personID: registrant.personID,
                                                eventInstanceID: targetEventInstanceID || eventInstanceID,
                                                locationID: effectiveLocationID || undefined,
                                            }).catch(err => console.error('Check-in error:', err))
                                        ));
                                        // Mark as checked-in and remove from selection
                                        setCheckedInRegistrantIDs(prev => new Set([...prev, ...selectedRegistrants.map(r => r.personID)]));
                                        setSelectedRegistrantIDs(new Set());
                                        // Show brief success toast but keep modal open
                                        Toast.show({
                                            type: 'success',
                                            text2: `Checked in ${selectedRegistrants.length} person${selectedRegistrants.length > 1 ? 's' : ''}`,
                                            position: 'top',
                                        });
                                    } catch (error) {
                                        console.error('Check-in failed:', error);
                                        Toast.show({
                                            type: 'error',
                                            text2: 'Check-in failed',
                                            position: 'top',
                                        });
                                    } finally {
                                        setIsCheckingIn(false);
                                    }
                                }}
                            />
                            <SolidButton
                                text="Check In All"
                                type="primary"
                                size="lg"
                                disabled={isCheckingIn}
                                processing={isCheckingIn}
                                onPress={async () => {
                                    setIsCheckingIn(true);
                                    try {
                                        // Check in all registrants
                                        await Promise.all(groupCheckInData.group.registrants.map(registrant =>
                                            checkInPerson({
                                                personID: registrant.personID,
                                                eventInstanceID: targetEventInstanceID || eventInstanceID,
                                                locationID: effectiveLocationID || undefined,
                                            }).catch(err => console.error('Check-in error:', err))
                                        ));
                                        resetGroupCheckIn();
                                        Toast.show({
                                            type: 'success',
                                            text2: `Checked in ${groupCheckInData.group.registrants.length} people`,
                                            position: 'top',
                                        });
                                    } catch (error) {
                                        console.error('Check-in failed:', error);
                                        Toast.show({
                                            type: 'error',
                                            text2: 'Check-in failed',
                                            position: 'top',
                                        });
                                    } finally {
                                        setIsCheckingIn(false);
                                    }
                                }}
                            />
                        </View>

                        {/* Registrant detail modal — nested inside group modal so iOS
                            presents it from the group modal's context (pageSheet stacking) */}
                        <RegistrantDetailModal
                            isOpen={!!detailRegistrant}
                            onClose={() => {
                                setDetailRegistrant(null);
                            }}
                            eventInstanceID={eventInstanceID}
                            targetEventInstanceID={targetEventInstanceID || undefined}
                            targetLabel={targetEventInstanceID && targetEventInstanceID !== eventInstanceID ? `${targetEventName || 'extended event'}${targetStartTime ? ` · ${formatTargetDate(targetStartTime)}` : ''}` : undefined}
                            registrant={detailRegistrant}
                            locations={checkInLocations}
                            registrantPaths={registrantPaths}
                            isCheckedIn={detailRegistrant ? checkedInRegistrantIDs.has(detailRegistrant.personID) : false}
                            onCheckedIn={(personID) => {
                                setCheckedInRegistrantIDs(prev => new Set([...prev, personID]));
                            }}
                        />
                    </View>
                </View>
            </Modal>

            {/* Registrant detail modal — standalone after badge scan (no group modal behind) */}
            {!groupCheckInData && (
                <RegistrantDetailModal
                    isOpen={!!detailRegistrant}
                    onClose={() => {
                        setDetailRegistrant(null);
                        resetScanner();
                    }}
                    eventInstanceID={eventInstanceID}
                    targetEventInstanceID={targetEventInstanceID || undefined}
                    targetLabel={targetEventInstanceID && targetEventInstanceID !== eventInstanceID ? `${targetEventName || 'extended event'}${targetStartTime ? ` · ${formatTargetDate(targetStartTime)}` : ''}` : undefined}
                    registrant={detailRegistrant}
                    locations={checkInLocations}
                    registrantPaths={registrantPaths}
                    isCheckedIn={detailRegistrant ? checkedInRegistrantIDs.has(detailRegistrant.personID) : false}
                    onCheckedIn={(personID) => {
                        setCheckedInRegistrantIDs(prev => new Set([...prev, personID]));
                    }}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        backgroundColor: 'black',
    },
    camera: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flexDirection: 'column',
        zIndex: 5,
    },
    darkArea: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.55)',
    },
    middleRow: {
        flexDirection: 'row',
        height: 280,
    },
    scanFrame: {
        width: 280,
        height: 280,
        position: 'relative',
        backgroundColor: 'transparent',
    },
    corner: {
        position: 'absolute',
        width: 36,
        height: 36,
        borderColor: 'white',
        borderWidth: 3,
    },
    cornerTopLeft: { top: 0, left: 0, borderRightWidth: 0, borderBottomWidth: 0 },
    cornerTopRight: { top: 0, right: 0, borderLeftWidth: 0, borderBottomWidth: 0 },
    cornerBottomLeft: { bottom: 0, left: 0, borderRightWidth: 0, borderTopWidth: 0 },
    cornerBottomRight: { bottom: 0, right: 0, borderLeftWidth: 0, borderTopWidth: 0 },
    instructionText: {
        position: 'absolute',
        bottom: 130,
        left: 0,
        right: 0,
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        fontFamily: fonts.medium,
        textAlign: 'center',
        zIndex: 5,
    },
    backButton: {
        position: 'absolute',
        top: 60,
        left: 20,
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(0,0,0,0.45)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
    },
    // Toast result
    toastContainer: {
        position: 'absolute',
        top: 120,
        left: 20,
        right: 20,
        alignItems: 'center',
    },
    toastCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(30,30,30,0.92)',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 12,
        maxWidth: 320,
    },
    toastSuccess: {
        borderLeftWidth: 3,
        borderLeftColor: colors.success,
    },
    toastError: {
        borderLeftWidth: 3,
        borderLeftColor: colors.error,
    },
    toastIcon: {
        flexShrink: 0,
    },
    iconSuccess: {
        color: colors.success,
    },
    iconError: {
        color: colors.error,
    },
    toastText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 14,
        fontFamily: fonts.medium,
        flexShrink: 1,
    },
    toastName: {
        fontFamily: fonts.heading,
        color: 'white',
    },
    // Permission screen
    permissionContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        backgroundColor: colors.background,
        gap: 16,
    },
    permissionTitle: {
        fontSize: 20,
        fontFamily: fonts.heading,
        color: colors.text,
        marginTop: 16,
    },
    permissionText: {
        fontSize: 16,
        color: colors.textSecondary,
        textAlign: 'center',
        marginBottom: 16,
    },
    backButtonAlt: {
        marginTop: 8,
    },
    backButtonText: {
        color: colors.textSecondary,
        fontSize: 16,
        fontFamily: fonts.medium,
    },
    // Group check-in modal
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: colors.background,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        paddingBottom: 60,
        maxHeight: '80%',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    modalTitle: {
        fontSize: 20,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    modalCloseButton: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.surface,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalCloseIcon: {
        color: colors.textSecondary,
    },
    modalScroll: {
        paddingHorizontal: 20,
        paddingTop: 16,
    },
    modalSubtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginBottom: 16,
    },
    targetBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: colors.primaryContainer,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        marginBottom: 16,
    },
    targetBannerText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
        flexShrink: 1,
    },
    registrantCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        marginBottom: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        borderWidth: 1,
        borderColor: colors.surface,
    },
    registrantMain: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    registrantInfoButton: {
        padding: 4,
    },
    registrantCardSelected: {
        backgroundColor: colors.successContainer,
        borderWidth: 1,
        borderColor: colors.success,
    },
    registrantCardCheckedIn: {
        backgroundColor: colors.surfaceVariant,
        opacity: 0.6,
    },
    registrantCheckbox: {
        width: 24,
        height: 24,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
    },
    registrantCheckboxChecked: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: colors.primary,
    },
    registrantInfo: {
        flex: 1,
    },
    registrantName: {
        fontSize: 16,
        fontFamily: fonts.medium,
        color: colors.text,
    },
    registrantNameCheckedIn: {
        color: colors.textSecondary,
    },
    registrantAge: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
    },
    checkedInLabel: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.success,
        marginTop: 2,
    },
    modalLocationSection: {
        marginTop: 24,
    },
    modalLocationLabel: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginBottom: 8,
    },
    modalLocationLabelCentered: {
        textAlign: 'center',
    },
    modalLocationPills: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
    },
    modalLocationPill: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 100,
        borderWidth: 1,
        borderColor: colors.border,
    },
    modalLocationPillSelected: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
    },
    modalLocationPillText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.text,
    },
    modalLocationPillTextSelected: {
        color: colors.onPrimary,
    },
    modalButtonsColumn: {
        flexDirection: 'column',
        gap: 12,
        marginHorizontal: 20,
        marginTop: 24,
    },
}));
